/** Legal copy aligned with WasOK `docs/PRIVACY_POLICY.md` / `docs/TERMS_OF_SERVICE.md`. */
export const legalMessagesEn = {
  privacy: {
    pageTitle: "Privacy Policy",
    lastUpdated: "Last updated: June 1, 2026",
    introduction: {
      title: "Introduction",
      body: "WasOK is a local-first shopping helper application designed to help users remember products they have previously purchased, rated, or reviewed.\n\nThis Privacy Policy explains what information is processed when using the app, how data is stored, and how certain third-party services may operate.",
    },
    dataStored: {
      title: "Data Stored by the App",
      body: "WasOK stores the following information locally on your device:\n\n• scanned product barcodes\n• product names and details entered by the user\n• ratings and comments\n• shopping lists\n• product categories\n• prices and store information\n• product and store photos\n\nYour product data and photos are not uploaded to WasOK servers.",
    },
    permissions: {
      title: "Permissions Used",
      camera: {
        title: "Camera",
        body: "Camera access is used for:\n\n• barcode scanning\n• taking product photos",
      },
      photos: {
        title: "Photos and Media",
        body: "Photo or gallery access is used only when:\n\n• attaching images to products\n• restoring backups containing photos",
      },
      files: {
        title: "File and Storage Access",
        body: "File access may be used for:\n\n• exporting backup files\n• importing backups selected by the user\n• sharing shopping lists or backups",
      },
    },
    backup: {
      title: "Backup and Export Features",
      body: "WasOK allows users to create backup files containing app data and optional photos.\n\nBackup files are created locally on your device and may be stored or shared using services selected by the user, such as:\n\n• Google Drive\n• Dropbox\n• iCloud Drive\n• local device storage\n\nWasOK does not upload backups to its own servers.",
      onlineProductLookup: {
        title: "Online product lookup",
        body: "When you enable online barcode lookup in Preferences, WasOK may send only the scanned barcode number to public product databases (such as Open Food Facts) to suggest a product name and brand. WasOK does not upload your ratings, comments, photos, shopping lists, categories, store information, or backup data for this feature. You can turn this off at any time in Preferences.",
      },
    },
    advertising: {
      title: "Advertising and Third-Party Services",
      intro:
        "WasOK uses selected third-party services to provide certain features.",
      admob: {
        title: "Google AdMob",
        body: "Google AdMob is used to display advertisements within the app.\n\nGoogle and its partners may process information such as:\n\n• advertising identifiers\n• device information\n• IP address\n• diagnostics and usage information\n\nUsers in certain regions may be asked for consent before personalized ads are shown.",
      },
      crashlytics: {
        title: "Firebase Crashlytics",
        body: "WasOK uses Firebase Crashlytics to help identify crashes and improve app stability.\n\nCrash reports may include technical information such as:\n\n• device model\n• operating system version\n• app version\n• crash diagnostics\n\nCrash reports do not intentionally include your product ratings, notes, shopping lists, or photos.",
      },
      externalProductDatabases: {
        title: "External Product Databases",
        body: "Barcode lookup functionality may use external product databases to retrieve product information.\n\nFor more information, please review the privacy policies of the respective providers.",
      },
    },
    dataSecurity: {
      title: "Data Security",
      body: "WasOK stores app data locally on your device using storage mechanisms provided by the operating system.\n\nWhile reasonable measures are taken to protect local app data, no method of electronic storage is completely secure.\n\nUsers are responsible for maintaining backups of important data if desired.",
    },
    children: {
      title: "Children's Privacy",
      body: "WasOK is not directed to children under the age of 13.",
    },
    notifications: {
      title: "Notifications",
      body: "WasOK may optionally create local notifications on your device to remind you to rate products after purchase.\n\nThese notifications are generated and scheduled locally on your device and do not require an account or remote server.\n\nYou can disable notifications at any time through your device settings or within the app, if supported.",
    },
    yourControl: {
      title: "Your Control Over Data",
      body: "You can:\n\n• edit or delete stored products at any time\n• clear app data from your device\n• uninstall the app\n• export backups of your data",
    },
    changes: {
      title: "Changes to This Policy",
      body: "This Privacy Policy may be updated from time to time.\n\nChanges become effective when the updated version is published.",
    },
    contact: {
      title: "Contact",
      body: "If you have questions regarding this Privacy Policy, please contact:\n\nsupport@wasok.app",
    },
  },
  terms: {
    pageTitle: "Terms of Service",
    lastUpdated: "Last updated: June 1, 2026",
    acceptance: {
      title: "Acceptance of Terms",
      body: "By downloading, installing, or using WasOK, you agree to these Terms of Service.\n\nIf you do not agree with these Terms, you should not use the app.",
    },
    license: {
      title: "License",
      body: "WasOK grants you a limited, non-exclusive, non-transferable, revocable license to use the app in accordance with these Terms.",
    },
    acceptableUse: {
      title: "Acceptable Use",
      body: "You agree not to:\n\n• misuse the app\n• interfere with the operation of the app\n• attempt to reverse engineer or modify the app\n• use the app in violation of applicable laws or regulations",
    },
    localData: {
      title: "Local Data and Backups",
      body: "WasOK stores user data locally on the device.\n\nUsers are responsible for maintaining backups of important data if desired.\n\nWhile backup and restore functionality may be provided, WasOK does not guarantee against loss, corruption, or failure of stored data.",
    },
    advertisements: {
      title: "Advertisements",
      body: "The app may display advertisements provided by third-party advertising partners, including Google AdMob.",
    },
    noWarranty: {
      title: "No Warranty",
      body: "WasOK is provided \"as is\" and \"as available\" without warranties of any kind, express or implied.\n\nWe do not guarantee that:\n\n• the app will always be available\n• the app will be error-free\n• data loss will never occur\n• all features will function uninterrupted on all devices",
    },
    limitationLiability: {
      title: "Limitation of Liability",
      body: "To the maximum extent permitted by law, WasOK and its developers shall not be liable for:\n\n• loss of data\n• loss of profits\n• device issues\n• indirect or consequential damages\n• interruptions resulting from use or inability to use the app",
    },
    thirdParty: {
      title: "Third-Party Services",
      body: "Certain features or content may rely on third-party services, including advertising providers and external storage/sharing services selected by the user.\n\nWasOK is not responsible for third-party services or their policies.",
    },
    productInformation: {
      title: "Product Information",
      body: "Certain product details may be obtained from third-party barcode lookup services.\n\nWasOK does not guarantee the accuracy, completeness, availability, or suitability of information provided by external data sources.\n\nUsers should independently verify product information when necessary.",
    },
    changesApp: {
      title: "Changes to the App",
      body: "WasOK may be modified, updated, suspended, or discontinued at any time without prior notice.",
    },
    changesTerms: {
      title: "Changes to These Terms",
      body: "These Terms may be updated periodically.\n\nContinued use of the app after updates become effective constitutes acceptance of the revised Terms.",
    },
    contact: {
      title: "Contact",
      body: "For questions regarding these Terms, contact:\n\nsupport@wasok.app",
    },
  },
};
